import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { isMobile } from '../helper/getMobileOS';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

function MyPDFViewer({ pdfUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchPageNumber, setSearchPageNumber] = useState(1);
  const [scale, setScale] = useState(isMobile() ? 0.5 : 1.0);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [loaded, setLoaded] = useState(-1);

  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);
  const [pageWidth, setPageWidth] = useState(600);
  const [screenHeight, setScreenHeight] = useState(200);

  // Update page width to match toolbar width
  useEffect(() => {
    const updateWidth = () => {
      if (toolbarRef.current) {
        const width = toolbarRef.current.offsetWidth;
        // const screenHeight = window.innerHeight;
        setPageWidth(width);
        setScreenHeight(1 * screenHeight);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const nextPage = () => {
    if (pageNumber < numPages) setPageNumber(pageNumber + 1);
  };

  const prevPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };
  const zoomIn = () => { setScale((prev) => prev + 0.2); };
  const zoomOut = () => { setScale((prev) => (prev > 0.6 ? prev - 0.2 : prev)); };

  // Drag handlers
  const onMouseDown = (e) => {
    if (scale <= 1) return; // Only allow panning when zoomed in
    isDragging.current = true;
    startX.current = e.pageX - containerRef.current.offsetLeft;
    startY.current = e.pageY - containerRef.current.offsetTop;
    scrollLeft.current = containerRef.current.scrollLeft;
    scrollTop.current = containerRef.current.scrollTop;
    containerRef.current.style.cursor = 'grabbing';
  };

  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;
    const walkX = x - startX.current;
    const walkY = y - startY.current;
    containerRef.current.scrollLeft = scrollLeft.current - walkX;
    containerRef.current.scrollTop = scrollTop.current - walkY;
  };

  const onMouseUp = () => {
    isDragging.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = scale > 1 ? 'grab' : 'default';
    }
  };

  const onMouseLeave = () => {
    isDragging.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = scale > 1 ? 'grab' : 'default';
    }
  };

  const currentLang = localStorage.getItem('ecupLang') ?? 'en';

  return (
    <div className="w-100 px-2">
      {/* Toolbar */}
      <div ref={toolbarRef} className="d-flex align-items-center justify-content-between mb-2">
        <button className="btn btn-danger btn-sm" title="Previous" onClick={prevPage}>
          {currentLang === 'en' ? (
            <i className="fa-solid fa-arrow-left w-unset"></i>
          ) : (
            <i className="fa-solid fa-arrow-right w-unset"></i>
          )}
        </button>

        <span>
          <input
            value={searchPageNumber}
            type="number"
            style={{ width: 80, textAlign: 'center', padding: 0 }}
            min={1}
            max={numPages}
            onChange={(e) => {
              if (e.target.value === '') {
                setSearchPageNumber(e.target.value);
                return;
              }
              const value = Number(e.target.value);
              if (isNaN(value)) {
                setSearchPageNumber(value);
                return;
              }
              if (value < 1 || value > numPages) return;
              setSearchPageNumber(value);
              setPageNumber(value);
            }}
          />{' '}
          / {numPages || '...'}
        </span>

        <div>
          <button className="btn btn-warning btn-sm me-2 text-white" onClick={zoomOut}>
            <i className="fa fa-search-minus w-unset"></i>
          </button>
          <button className="btn btn-success btn-sm" onClick={zoomIn}>
            <i className="fa fa-search-plus w-unset"></i>
          </button>

          <button className="btn btn-danger btn-sm ms-2" title="Next" onClick={nextPage}>
            {currentLang === 'en' ? (
              <i className="fa-solid fa-arrow-right w-unset"></i>
            ) : (
              <i className="fa-solid fa-arrow-left w-unset"></i>
            )}
          </button>
        </div>
      </div>

      {loaded < totalLoaded && (
        <div className="progress mb-2">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${(loaded / totalLoaded) * 100}%` }}
            aria-valuenow="100"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      )}

      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadProgress={({ loaded, total }) => {
          setTotalLoaded(total);
          setLoaded(loaded);
        }}
        loading={<></>}
      >
        <div
          ref={containerRef}
          style={{
            // width: pageWidth,
            // maxHeight: screenHeight,
            overflowX: 'auto',
            overflowY: 'auto',
            cursor: scale > 1 ? 'grab' : 'default',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            backgroundColor: 'transparent',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          <div style={{ margin: 'auto' }}>
            <Page
              pageNumber={pageNumber}
              scale={scale}
              // width={pageWidth}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={<></>}
            />
          </div>
        </div>
      </Document>
    </div>
  );
}

export default MyPDFViewer;
